import React, { useState } from "react";
import styles from "./_customer-references.module.scss";
import { Button } from "@progress/kendo-react-buttons";
import { isNotNil } from "ramda";
import { Card, CardActions, CardHeader, Typography } from "@mui/material";
import { CardBody } from "@progress/kendo-react-layout";
import { Checkbox, TextArea, TextAreaChangeEvent, TextBox } from "@progress/kendo-react-inputs";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import classNames from "classnames";
import { formatPhoneNumber, hasValue, PermissionAccessLevel, PermissionSet, PhoneInput } from "@amsanalytics/react-shared-library";
import { buildDefaultCustomerReference, ICustomerReference } from "../../../../Interfaces/Customer/ICustomerReference";
import { AmsReferenceRelationshipType, ICustomerReferenceRelationship, ICustomerReferenceRelationshipWithTypeString } from "../../../../Interfaces/Common/ICustomerReferenceRelationship";
import { isGuid, userHasPermission } from "../../../../utils/utils";
import { IState } from "../../../../Interfaces/Common/IState";
import { ILoggedInEmployee } from "../../../../Interfaces/Admin/ILoggedInEmployee";
import { permissions } from "../../../../utils/constants";


export interface CustomerReferencesProps {
  currentUser: ILoggedInEmployee;
  customerId: string;
  applicationStatusId: string;
  amsCustomerNumber?: string;
  references: ICustomerReference[]; // the references to display
  customerReferenceRelationships: ICustomerReferenceRelationship[]; // the relationships to display in the dropdown
  states: IState[]; // the states to display in the dropdown
  width: number; // the width of the container
  canPushReferencesFromUiToAms: boolean;
  isEditable: boolean;
  isReadOnly: boolean;
  onCreateReference: (customerReference: ICustomerReference) => void;
  onUpdateReference: (customerReference: ICustomerReference) => void;
  onDeleteReference: (customerReferenceId: string) => void;
  onPushReferencesToAms?: (customerReferences: ICustomerReference[]) => void;
  onPushAllReferencesToAms?: () => void;
  onUpdateReferenceVerification: (customerReferenceId: string, isVerified: boolean) => void;
  applicationDate: Date;
}

export default function CustomerReferences(props: CustomerReferencesProps) {
  // ID of the reference object that is being removed. we will prompt the user to confirm the removal
  const [referenceIdToConfirmRemoval, setReferenceIdToConfirmRemoval] = useState<string>();
  // the reference object that is in "edit" mode
  const [referenceEditItem, setReferenceEditItem] = useState<ICustomerReference>();
  // If the relationship's deleted date is undefined or the deleted date happened after the application was created, add it to the list
  const customerReferenceRelationshipsWithTypeStrings: ICustomerReferenceRelationshipWithTypeString[] = props.customerReferenceRelationships
    .filter(crr => !hasValue(crr.deletedDate) || new Date(crr.deletedDate!).getTime() > new Date(props.applicationDate!).getTime())
    .map(crr => {
      return {
        ...crr,
        typeWithName: `${crr.amsReferenceRelationshipType === AmsReferenceRelationshipType.P ? "Personal" : "Credit"}- ${crr.name}`
      };
    });

  // whether or not the user can save the reference. Validation requirements are:
  // 1. Name
  // 2. Relationship
  // 3. Cell phone with parentheses and dashes stripped
  // 4. State
  const canSaveReference = (reference: ICustomerReference) => {
    return hasValue(reference.name)
      && isNotNil(reference.customerReferenceRelationshipId)
      && hasValue(reference.customerReferenceRelationshipId)
      && (hasValue(reference.cellPhone) && reference.cellPhone.replace(/[^0-9]/g, '').length === 10);
  }

  // Builds the header for the summary card
  const getSummaryCardHeader = (reference: ICustomerReference): string => {
    let header = reference.name;
    if (reference.customerReferenceRelationshipId) {
      const relationship = props.customerReferenceRelationships.find(crr => crr.id === reference.customerReferenceRelationshipId);
      if (relationship) {
        header += ` • ${relationship.name}`;
      }
    }
    return header;
  }

  // Constructs the address string for a reference
  const getAddressString = (reference: ICustomerReference): string => {
    let addressString = '';
    if (reference.address) {
      addressString += reference.address;
    }
    if (reference.city) {
      addressString += addressString ? `, ${reference.city}` : reference.city;
    }
    if (reference.stateId) {
      const state = props.states.find(s => s.id === reference.stateId);
      addressString += addressString && state ? `, ${state.name}` : state?.name;
    }
    if (reference.zip) {
      addressString += addressString ? ` ${reference.zip}` : reference.zip;
    }
    return addressString;
  }

  // Returns true if the current employee has permission to verify the reference. They can verify if:
  // they have the 'references' permission with 'U' access level or they created the reference
  const canVerifyReference = (reference: ICustomerReference): boolean => {
    return userHasPermission(props.currentUser, permissions.verifyReferences, PermissionSet.U, PermissionAccessLevel.All) || reference.createdBy.toLowerCase() === props.currentUser.id.toLowerCase();
  }

  return (
    <div className={props.isReadOnly ? styles.customerReferencesContainerDisabled : styles.customerReferencesContainer}>
      <div style={{ display: "flex", alignItems: "center", marginBottom: '10px' }}>
        {userHasPermission(props.currentUser, permissions.references, PermissionSet.C, PermissionAccessLevel.All) && (
          <Button
            themeColor='primary'
            className={styles.addReferenceBtn}
            style={props.canPushReferencesFromUiToAms ? { marginRight: "10px" } : {}}
            icon="plus"
            disabled={referenceEditItem !== undefined}
            onClick={() => setReferenceEditItem(buildDefaultCustomerReference(props.references.length.toString(), props.customerId, props.currentUser.id))}
          >
            Add Reference
          </Button>
        )}
        {props.canPushReferencesFromUiToAms &&
          <Button
            fillMode={'outline'}
            themeColor={'primary'}
            icon="upload"
            onClick={() => props.onPushAllReferencesToAms && props.onPushAllReferencesToAms()}
          >
            Push / Update All References to AMS
          </Button>
        }
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', flex: 1, gap: 5 }}>
        {[...(isNotNil(referenceEditItem) ? [referenceEditItem] : []), ...props.references.filter(r => r.id !== referenceEditItem?.id)]
          .map((reference: ICustomerReference, index: number) => {
            return referenceEditItem?.id === reference.id ? (
              <div key={index}>
                <Card className={styles.cardContainer}>
                  <CardBody>
                    <div className={props.width < 600 ? styles.cardBodyMobile : styles.cardBody}>
                      <div className={props.width > 600 ? styles.inputCol : styles.inputColFullWidth}>
                        <div>Name*</div>
                        <TextBox
                          value={reference.name}
                          onChange={(e) => {
                            setReferenceEditItem({ ...reference, name: e.value as string });
                          }}
                          placeholder='Name...'
                          fillMode="outline"
                        />
                      </div>
                      <div className={props.width > 600 ? classNames(styles.inputCol, styles.dropdown) : styles.inputColFullWidth}>
                        <div>Relationship*</div>
                        <DropDownList
                          fillMode="outline"
                          data={customerReferenceRelationshipsWithTypeStrings}
                          value={customerReferenceRelationshipsWithTypeStrings.find(r => r.id === reference.customerReferenceRelationshipId)}
                          dataItemKey={'id'}
                          textField={'name'}
                          onChange={(e) => {
                            setReferenceEditItem({ ...reference, customerReferenceRelationshipId: e.value.id as string });
                          }}
                          style={{ width: '100%' }}
                        />
                      </div>
                      <div className={props.width > 600 ? styles.inputCol : styles.inputColFullWidth}>
                        <div>Email</div>
                        <TextBox
                          value={reference.emailAddress}
                          onChange={(e) => {
                            setReferenceEditItem({ ...reference, emailAddress: e.value as string });
                          }}
                          placeholder='Email...'
                          fillMode="outline"
                        />
                      </div>
                      <div className={props.width > 600 ? styles.inputCol : styles.inputColFullWidth}>
                        <PhoneInput
                          value={reference.homePhone}
                          onChangeValue={(e: string) => {
                            setReferenceEditItem({ ...reference, homePhone: e as string });
                          }}
                          fillMode="outline"
                          label='Home Phone'
                        />
                      </div>
                      <div className={props.width > 600 ? styles.inputCol : styles.inputColFullWidth}>
                        <PhoneInput
                          value={reference.cellPhone}
                          onChangeValue={(e: string) => {
                            // strip underscores from phone number
                            setReferenceEditItem({ ...reference, cellPhone: e.replace(/_/g, '') });
                          }}
                          fillMode="outline"
                          label='Cell Phone*'
                        />
                      </div>
                      <div className={props.width > 600 ? styles.inputCol : styles.inputColFullWidth}>
                        <div>Address</div>
                        <TextBox
                          value={reference.address}
                          onChange={(e) => {
                            setReferenceEditItem({ ...reference, address: e.value as string });
                          }}
                          placeholder='Address...'
                          fillMode="outline"
                        />
                      </div>
                      <div className={props.width > 600 ? styles.inputCol : styles.inputColFullWidth}>
                        <div>City</div>
                        <TextBox
                          value={reference.city}
                          onChange={(e) => {
                            setReferenceEditItem({ ...reference, city: e.value as string });
                          }}
                          placeholder='City...'
                          fillMode="outline"
                        />
                      </div>
                      <div className={props.width > 600 ? classNames(styles.inputCol, styles.dropdown) : styles.inputColFullWidth}>
                        <div>State</div>
                        <DropDownList
                          fillMode="outline"
                          data={props.states.sort((a, b) => a.name.localeCompare(b.name))}
                          value={props.states.find(s => s.id === reference.stateId)}
                          dataItemKey={'id'}
                          textField={'name'}
                          onChange={(e) => {
                            setReferenceEditItem({ ...reference, stateId: e.value.id as string });
                          }}
                          style={{ width: '100%' }}
                        />
                      </div>
                      <div className={styles.flexRowFullWidth} style={{ justifyContent: 'space-between', gap: 9 }}>
                        <div className={props.width > 600 ? styles.inputCol : styles.inputColFullWidth}>
                          <div>
                            <div>Zip Code</div>
                            <TextBox
                              value={reference.zip}
                              type='number'
                              onChange={(e) => {
                                setReferenceEditItem({ ...reference, zip: e.value as string });
                              }}
                              placeholder='Zip Code...'
                              fillMode="outline"
                            />
                          </div>
                        </div>
                        <div className={styles.flexRow} style={{ flex: 1, gap: '10px' }}>
                          <Checkbox
                            label="Verified"
                            title="Verified"
                            checked={reference.isVerified}
                            disabled={!canVerifyReference(reference)}
                            onChange={(event) => {
                              // dispatch action to update isVerified for this reference is it is being edited.
                              if (isGuid(reference.id)) {
                                props.onUpdateReferenceVerification(reference.id, event.value);
                              }
                              setReferenceEditItem({ ...reference, isVerified: event.value });
                            }}
                          />
                          <Checkbox
                            label="Callable?"
                            title="Callable?"
                            checked={reference.callable}
                            onChange={(event) => {
                              setReferenceEditItem({ ...reference, callable: event.value });
                            }}
                          />
                          <Checkbox
                            label="Lives with Customer?"
                            title="Lives with Customer?"
                            checked={reference.livesWith}
                            onChange={(event) => {
                              setReferenceEditItem({ ...reference, livesWith: event.value });
                            }}
                          />
                        </div>
                      </div>
                      <div className={props.width > 600 ? styles.inputCol : styles.inputColFullWidth}>
                        <div>Notes</div>
                        <TextArea
                          value={reference.note || ''}
                          rows={2}
                          onChange={(e: TextAreaChangeEvent) => {
                            setReferenceEditItem({ ...reference, note: e.value as string });
                          }}
                          placeholder='Notes...'
                          fillMode="outline"
                        />
                      </div>
                      {isGuid(reference.id) && (
                        <small className={styles.inputColFullWidth} style={{ wordWrap: 'break-word' }}>
                          Created by {reference.createdBy} on {new Date(reference.createdDate).toLocaleDateString()}
                        </small>
                      )}
                    </div>
                  </CardBody>
                  <CardActions style={{ margin: 0, padding: '0px 1em 5px 1em' }}>
                    {referenceIdToConfirmRemoval !== reference.id && (
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span style={{ textAlign: 'left' }}>* required fields</span>
                        <div>
                          <Button
                            themeColor={'primary'}
                            disabled={!canSaveReference(reference)}
                            fillMode="flat"
                            style={{
                              alignSelf: 'flex-start',
                              width: 'fit-content',
                            }}
                            onClick={() => {
                              isGuid(reference.id)
                                ? props.onUpdateReference({ ...reference, stateId: props.states.find(s => s.id.toLowerCase() === reference.stateId?.toLowerCase())?.id })
                                : props.onCreateReference({ ...reference, stateId: props.states.find(s => s.id.toLowerCase() === reference.stateId?.toLowerCase())?.id })
                              setReferenceEditItem(undefined);
                              setReferenceIdToConfirmRemoval(undefined);
                            }}
                          >
                            {isGuid(reference.id) ? 'Save' : 'Create'}
                          </Button>
                          <Button
                            themeColor={'error'}
                            fillMode="flat"
                            style={{
                              alignSelf: 'flex-start',
                              width: 'fit-content',
                            }}
                            onClick={() => {
                              setReferenceEditItem(undefined);
                            }}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    )}
                    {referenceIdToConfirmRemoval === reference.id && (
                      <div>
                        Are you sure you want to remove this reference?
                        &nbsp;&nbsp;
                        <Button
                          themeColor={'error'}
                          fillMode="flat"
                          style={{
                            alignSelf: 'flex-start',
                            width: 'fit-content',
                          }}
                          onClick={() => {
                            if (isGuid(reference.id)) {
                              props.onDeleteReference(reference.id);
                            } else {
                              setReferenceEditItem(undefined);
                            }
                            setReferenceIdToConfirmRemoval(undefined);
                          }}
                        >
                          Yes
                        </Button>
                        <Button
                          themeColor={'base'}
                          fillMode="flat"
                          style={{
                            alignSelf: 'flex-start',
                            width: 'fit-content',
                          }}
                          onClick={() =>
                            setReferenceIdToConfirmRemoval(undefined)
                          }
                        >
                          No
                        </Button>
                      </div>
                    )}
                  </CardActions>
                </Card>
              </div>
            ) : (
              <div key={index} className={styles.summaryCardcontainer}>
                <Card style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                  <CardHeader
                    className={styles.summaryHeader}
                    title={<Typography variant="h6" style={{ fontWeight: "bold" }}>{getSummaryCardHeader(reference)}</Typography>}
                  />
                  <CardBody className={styles.summaryBody}>
                    {reference.emailAddress && (
                      <div>
                        Email:&nbsp;{reference.emailAddress}
                      </div>
                    )}
                    {reference.homePhone && (
                      <div>
                        Home Phone: {formatPhoneNumber(reference.homePhone)}
                      </div>
                    )}
                    {reference.cellPhone && (
                      <div>
                        Cell Phone: {formatPhoneNumber(reference.cellPhone)}
                      </div>
                    )}
                    <div>
                      {getAddressString(reference)}
                    </div>
                    <div>
                      Can Call: {reference.callable ? 'Yes' : 'No'}
                    </div>
                    <div>
                      Lives With Customer: {reference.livesWith ? 'Yes' : 'No'}
                    </div>
                    <div>
                      <Checkbox
                        label="Verified"
                        title="Verified"
                        checked={reference.isVerified}
                        disabled={!canVerifyReference(reference)}
                        onChange={(event) => {
                          // dispatch action to update isVerified for this reference is it is being edited.
                          if (isGuid(reference.id)) {
                            props.onUpdateReferenceVerification(reference.id, event.value);
                          }
                        }}
                      />

                      <small style={{ wordWrap: 'break-word' }}>
                        Created by {reference.createdBy} on {new Date(reference.createdDate).toLocaleDateString()}
                      </small>
                      <br />
                      {reference.isVerified && <small style={{ wordWrap: 'break-word' }}>Verified by {reference.modifiedBy} on {new Date(reference.modifiedDate).toLocaleDateString()}</small>}
                    </div>
                    {reference.note && reference.note.length > 0 && (
                      <div style={{ wordWrap: 'break-word' }}>Notes:&nbsp;{reference.note}</div>
                    )}
                  </CardBody>
                  <CardActions className={styles.summaryActions}>
                    {referenceIdToConfirmRemoval !== reference.id && (
                      <div>
                        {props.canPushReferencesFromUiToAms &&
                          <Button
                            themeColor={'base'}
                            fillMode="flat"
                            style={{
                              alignSelf: 'flex-start',
                              width: 'fit-content',
                            }}
                            onClick={() => props.onPushReferencesToAms && props.onPushReferencesToAms([reference])}>
                            {hasValue(reference.sentToAmsDate) ? 'Push Update to AMS' : 'Push to AMS'}
                          </Button>
                        }
                        {userHasPermission(props.currentUser, permissions.references, PermissionSet.U, PermissionAccessLevel.All) && (
                          <Button
                            themeColor={'primary'}
                            fillMode="flat"
                            disabled={isNotNil(referenceEditItem)}
                            style={{
                              alignSelf: 'flex-start',
                              width: 'fit-content',
                            }}
                            onClick={() => {
                              setReferenceEditItem({ ...reference });
                            }}
                          >
                            Edit
                          </Button>
                        )}
                        {userHasPermission(props.currentUser, permissions.references, PermissionSet.D, PermissionAccessLevel.All) && (
                          <Button
                            themeColor={'error'}
                            fillMode="flat"
                            style={{
                              alignSelf: 'flex-start',
                              width: 'fit-content',
                            }}
                            onClick={() =>
                              setReferenceIdToConfirmRemoval(reference.id)
                            }
                          >
                            Remove
                          </Button>
                        )}
                      </div>
                    )}
                    {referenceIdToConfirmRemoval === reference.id && (
                      <div>
                        Are you sure you want to remove this reference?
                        &nbsp;&nbsp;
                        <Button
                          themeColor={'error'}
                          fillMode="flat"
                          style={{
                            alignSelf: 'flex-start',
                            width: 'fit-content',
                          }}
                          onClick={() => {
                            if (isGuid(reference.id)) {
                              props.onDeleteReference(reference.id);
                            } else {
                              setReferenceEditItem(undefined);
                            }
                            setReferenceIdToConfirmRemoval(undefined);
                          }}
                        >
                          Yes
                        </Button>
                        <Button
                          themeColor={'base'}
                          fillMode="flat"
                          style={{
                            alignSelf: 'flex-start',
                            width: 'fit-content',
                          }}
                          onClick={() =>
                            setReferenceIdToConfirmRemoval(undefined)
                          }
                        >
                          No
                        </Button>
                      </div>
                    )}
                  </CardActions>
                </Card>
              </div>
            );
          })}
      </div>
    </div >
  );
}
